import { Label } from "layout/typography/Label";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { useMemo, useState } from "react";
import { useGetBenefitFallbackCasesQuery } from "store/queries/benefitFallbackCases";
import styled from "styled-components";
import {
  FallbackCaseBenefit,
  FallbackCaseRung,
  IFallbackCaseRow,
} from "types/BenefitFallbackCases/BenefitFallbackCases";

import {
  benefitInWords,
  cameFromInWords,
  checkedAtInWords,
  defaultFallbackCasesRange,
  estimateLink,
  payerPlanInWords,
  usedOnEstimateInWords,
  valueInWords,
} from "./fallbackCasesWording";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
`;

/** The filters' "every" choice. A Dropdown cannot hold undefined as a distinct option value. */
const EVERY = "__every__";

export const FALLBACK_CASES_PAGE_SIZE = 50;

export const FALLBACK_CASES_INTRO =
  "Every estimate whose co-insurance or copay did not come from the hospital's own service line: it was substituted from another service line's benefit, or taken from earlier checks of the same plan by other patients. The rules behind these are in “How fallbacks work” above. Read only.";

export const FALLBACK_CASES_EMPTY =
  "No estimate in this window had its co-insurance or copay filled from another service line or from earlier patients.";

const BENEFIT_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "Every benefit", value: EVERY },
  { label: "Co-insurance", value: "COINSURANCE_PERCENT" },
  { label: "Copay", value: "FLAT_COPAY" },
  { label: "Physician copay", value: "PROFESSIONAL_COPAY" },
];

const CAME_FROM_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "Anywhere", value: EVERY },
  { label: "Another service line", value: "SUBSTITUTE" },
  { label: "Earlier patients", value: "HISTORY" },
];

const orUndefined = <T extends string>(value: string): T | undefined =>
  value === EVERY ? undefined : (value as T);

interface IFallbackCasesTabProps {
  /** Today, for the default window. A prop so tests can pin it. */
  now?: Date;
}

/**
 * CLE-3047 — Eligibility › Fallbacks, the checks list (`GET /eligibility/benefit-fallback-cases`): every LIVE eligibility check
 * whose co-insurance or copay came from another service line (SUBSTITUTE) or from earlier checks of other patients on
 * the same plan (HISTORY), with the engine's own sentence for why and a link to the estimate. The "How fallbacks work" section above
 * shows the rules; this shows the cases. Filtered and paged on the server, newest first. The default view is what patients
 * actually saw ("Used on the estimate only"); unticking it adds the engine's shadow picks on checks legacy quoted.
 * Read only; SuperAdmin like every tab on this page, and the API is the boundary.
 */
export function FallbackCasesTab({ now }: IFallbackCasesTabProps) {
  const [initialRange] = useState(() => defaultFallbackCasesRange(now));
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [hospital, setHospital] = useState<string>(EVERY);
  const [benefit, setBenefit] = useState<string>(EVERY);
  const [cameFrom, setCameFrom] = useState<string>(EVERY);
  const [usedOnly, setUsedOnly] = useState(true);
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, isError } =
    useGetBenefitFallbackCasesQuery(
      {
        from,
        to,
        accountId: orUndefined<string>(hospital),
        benefit: orUndefined<FallbackCaseBenefit>(benefit),
        cameFrom: orUndefined<FallbackCaseRung>(cameFrom),
        usedOnly,
        page,
        pageSize: FALLBACK_CASES_PAGE_SIZE,
      },
      { refetchOnMountOrArgChange: true }
    );

  /** Every filter change goes back to the first page. */
  const filter =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const hospitalOptions = useMemo(
    () => [
      { label: "Every hospital", value: EVERY },
      ...(data?.accounts ?? []).map((account) => ({
        label: account.name,
        value: account.id,
      })),
    ],
    [data]
  );

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  const payerPlanBody = (row: IFallbackCaseRow) => {
    const { payer, plan } = payerPlanInWords(row);
    return (
      <div className="flex flex-column gap-1">
        <span>{payer}</span>
        {plan && <small className="text-color-secondary">{plan}</small>}
      </div>
    );
  };

  const usedBody = (row: IFallbackCaseRow) => (
    <span
      className={row.authoritative ? "font-semibold" : "text-color-secondary"}
    >
      {usedOnEstimateInWords(row)}
    </span>
  );

  const estimateBody = (row: IFallbackCaseRow) => {
    const href = estimateLink(row);
    return href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open estimate (opens in a new tab)"
        className="white-space-nowrap"
      >
        Open estimate{" "}
        <i className="pi pi-external-link text-xs" aria-hidden="true" />
      </a>
    ) : (
      <span className="text-color-secondary">No estimate</span>
    );
  };

  return (
    <div className="flex flex-column gap-3" data-testid="fallbackCasesTab">
      <Surface>
        <p
          className="mt-0 mb-0 text-color-secondary"
          data-testid="fallbackCases.intro"
        >
          {FALLBACK_CASES_INTRO}
        </p>
      </Surface>

      <Surface>
        <div className="flex flex-wrap gap-3 mb-2 align-items-end">
          <div className="flex flex-column gap-1">
            <Label htmlFor="fallbackCases.hospital">Hospital</Label>
            <Dropdown
              inputId="fallbackCases.hospital"
              options={hospitalOptions}
              value={hospital}
              onChange={(event) => filter(setHospital)(event.value || EVERY)}
              filter
              style={{ minWidth: "16rem" }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <Label htmlFor="fallbackCases.from">From</Label>
            <input
              id="fallbackCases.from"
              data-testid="fallbackCases.from"
              className="p-inputtext"
              type="date"
              value={from}
              max={to}
              onChange={(event) =>
                event.target.value && filter(setFrom)(event.target.value)
              }
            />
          </div>
          <div className="flex flex-column gap-1">
            <Label htmlFor="fallbackCases.to">To</Label>
            <input
              id="fallbackCases.to"
              data-testid="fallbackCases.to"
              className="p-inputtext"
              type="date"
              value={to}
              min={from}
              onChange={(event) =>
                event.target.value && filter(setTo)(event.target.value)
              }
            />
          </div>
          <div className="flex flex-column gap-1">
            <Label htmlFor="fallbackCases.benefit">Benefit</Label>
            <Dropdown
              inputId="fallbackCases.benefit"
              options={BENEFIT_OPTIONS}
              value={benefit}
              onChange={(event) => filter(setBenefit)(event.value || EVERY)}
              style={{ minWidth: "12rem" }}
            />
          </div>
          <div className="flex flex-column gap-1">
            <Label htmlFor="fallbackCases.cameFrom">Where it came from</Label>
            <Dropdown
              inputId="fallbackCases.cameFrom"
              options={CAME_FROM_OPTIONS}
              value={cameFrom}
              onChange={(event) => filter(setCameFrom)(event.value || EVERY)}
              style={{ minWidth: "14rem" }}
            />
          </div>
          <div className="flex align-items-center gap-2 pb-2">
            <Checkbox
              inputId="fallbackCases.usedOnly"
              checked={usedOnly}
              onChange={(event) => filter(setUsedOnly)(Boolean(event.checked))}
            />
            <label htmlFor="fallbackCases.usedOnly">
              Used on the estimate only
            </label>
          </div>
        </div>

        {isLoading && (
          <div className="flex justify-content-center p-3">
            <ProgressSpinner
              style={{ width: "2rem", height: "2rem" }}
              data-testid="fallbackCases.loading"
            />
          </div>
        )}
        {isError && (
          <Message
            severity="error"
            text="The checks could not be loaded."
            data-testid="fallbackCases.error"
          />
        )}
        {!isLoading && !isError && data && total === 0 && (
          <Message
            severity="info"
            text={FALLBACK_CASES_EMPTY}
            data-testid="fallbackCases.empty"
          />
        )}
        {!isLoading && !isError && total > 0 && (
          <>
            <p className="mt-0 mb-2" data-testid="fallbackCases.total">
              {total.toLocaleString("en-US")} {total === 1 ? "case" : "cases"}
            </p>
            <StyledDataTable
              value={rows}
              dataKey="pickId"
              className="w-full"
              data-testid="fallbackCases.table"
              lazy
              paginator
              rows={FALLBACK_CASES_PAGE_SIZE}
              first={(page - 1) * FALLBACK_CASES_PAGE_SIZE}
              totalRecords={total}
              onPage={(event) =>
                setPage(Math.floor(event.first / FALLBACK_CASES_PAGE_SIZE) + 1)
              }
              loading={isFetching}
            >
              <Column
                header="Date of check"
                body={(row: IFallbackCaseRow) => checkedAtInWords(row)}
                style={{ width: "10rem" }}
              />
              <Column
                header="Hospital"
                field="accountName"
                style={{ width: "12rem" }}
              />
              <Column header="Payer and plan" body={payerPlanBody} />
              <Column
                header="Benefit"
                body={(row: IFallbackCaseRow) => benefitInWords(row)}
                style={{ width: "9rem" }}
              />
              <Column
                header="Value"
                body={(row: IFallbackCaseRow) => valueInWords(row)}
                style={{ width: "6rem" }}
              />
              <Column
                header="Where it came from"
                body={(row: IFallbackCaseRow) => cameFromInWords(row)}
              />
              <Column header="Why" field="reason" />
              <Column
                header="Used on the estimate"
                body={usedBody}
                style={{ width: "9rem" }}
              />
              <Column
                header="Estimate"
                body={estimateBody}
                style={{ width: "8rem" }}
              />
            </StyledDataTable>
          </>
        )}
      </Surface>
    </div>
  );
}

FallbackCasesTab.defaultProps = { now: undefined };
